import React, { useCallback, useState, useRef, Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Mail, FileText, Settings, Database, PieChart, MessageSquare, Globe, Bell, ChevronDown, ChevronRight, LogOut, Building2, MapPin, Tag, FolderOpen, Key, Zap, AlertTriangle, Clock, Send, UserCircle, Shield } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { ConfirmModal } from './ConfirmModal';
interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  section?: string;
  /** Short label for collapsed rail (max ~8 chars) */
  shortName?: string;
}
const RAIL_WIDTH = 62;
const EXPANDED_WIDTH = 264;
const COLLAPSE_DELAY = 180;
const navigation: NavItem[] = [{
  name: 'Dashboard',
  href: '/dashboard',
  icon: LayoutDashboard,
  shortName: 'Home'
}, {
  name: 'Mailing Lists',
  href: '/mailing-lists',
  icon: Database,
  section: 'CONTENT',
  shortName: 'Lists'
}, {
  name: 'Newsletters',
  href: '/newsletters',
  icon: MessageSquare,
  section: 'CONTENT',
  shortName: 'News'
}, {
  name: 'Contacts',
  href: '/contacts',
  icon: UserCircle,
  section: 'CONTENT',
  shortName: 'Contacts'
}, {
  name: 'Signup Forms',
  href: '/forms',
  icon: FileText,
  section: 'FORMS & TEMPLATES',
  shortName: 'Forms'
}, {
  name: 'Form Templates',
  href: '/form-templates',
  icon: ChevronRight,
  section: 'FORMS & TEMPLATES',
  shortName: 'F.Templ'
}, {
  name: 'Email Templates',
  href: '/templates',
  icon: Mail,
  section: 'FORMS & TEMPLATES',
  shortName: 'Email T.'
}, {
  name: 'Email Library',
  href: '/email-templates-library',
  icon: Globe,
  section: 'FORMS & TEMPLATES',
  shortName: 'Library'
}, {
  name: 'Landing Pages',
  href: '/landing-pages',
  icon: Globe,
  section: 'FORMS & TEMPLATES',
  shortName: 'Pages'
}, {
  name: 'Media Library',
  href: '/media-library',
  icon: Building2,
  section: 'FORMS & TEMPLATES',
  shortName: 'Media'
}, {
  name: 'Form Configuration',
  href: '/configurations',
  icon: Settings,
  section: 'FORMS & TEMPLATES',
  shortName: 'Config'
}, {
  name: 'Campaigns',
  href: '/campaigns',
  icon: Send,
  section: 'CAMPAIGNS',
  shortName: 'Camps'
}, {
  name: 'Audiences',
  href: '/audiences',
  icon: Tag,
  section: 'CAMPAIGNS',
  shortName: 'Audience'
}, {
  name: 'Send Buckets',
  href: '/send-buckets',
  icon: Database,
  section: 'CAMPAIGNS',
  shortName: 'Buckets'
}, {
  name: 'Tags',
  href: '/tags',
  icon: Tag,
  section: 'ORGANIZATION',
  shortName: 'Tags'
}, {
  name: 'Categories',
  href: '/categories',
  icon: FolderOpen,
  section: 'ORGANIZATION',
  shortName: 'Categs'
}, {
  name: 'Regions',
  href: '/regions',
  icon: MapPin,
  section: 'ORGANIZATION',
  shortName: 'Regions'
}, {
  name: 'Submissions',
  href: '/submissions',
  icon: Users,
  section: 'DATA',
  shortName: 'Submits'
}, {
  name: 'Subscriptions',
  href: '/subscriptions',
  icon: Users,
  section: 'DATA',
  shortName: 'Subs'
}, {
  name: 'Audit Log',
  href: '/audit-log',
  icon: Clock,
  section: 'DATA',
  shortName: 'Audit'
}, {
  name: 'Team Users',
  href: '/users',
  icon: Users,
  section: 'ADMIN',
  shortName: 'Users'
}, {
  name: 'Roles & Permissions',
  href: '/roles',
  icon: Key,
  section: 'ADMIN',
  shortName: 'Roles'
}, {
  name: 'Account Settings',
  href: '/account',
  icon: Shield,
  section: 'ADMIN',
  shortName: 'Account'
}, {
  name: 'Settings',
  href: '/settings',
  icon: Settings,
  section: 'ADMIN',
  shortName: 'Settings'
}, {
  name: 'API Test',
  href: '/api-test',
  icon: Zap,
  section: 'ADMIN',
  shortName: 'API'
}, {
  name: 'Error Log',
  href: '/error-log',
  icon: AlertTriangle,
  section: 'ADMIN',
  shortName: 'Errors'
}];
export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentUser,
    logout
  } = useWorkspace();
  const [expanded, setExpanded] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const primaryColor = currentWorkspace.primaryColor || '#2563eb';
  const handleMouseEnter = useCallback(() => {
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    setExpanded(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    collapseTimer.current = setTimeout(() => {
      setExpanded(false);
      collapseTimer.current = null;
    }, COLLAPSE_DELAY);
  }, []);
  const handleNavClick = useCallback((href: string) => {
    navigate(href);
    setExpanded(false);
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
  }, [navigate]);
  // Build section-grouped items for expanded view
  let lastSection: string | undefined = undefined;
  return <>
      {/* Fixed sidebar container */}
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="fixed left-0 top-0 h-screen z-40 bg-white border-r border-gray-200 flex flex-col overflow-hidden select-none" style={{
      width: expanded ? EXPANDED_WIDTH : RAIL_WIDTH,
      transition: 'width 220ms cubic-bezier(0.4, 0, 0.2, 1)',
      boxShadow: expanded ? '4px 0 24px rgba(0, 0, 0, 0.08), 1px 0 4px rgba(0, 0, 0, 0.04)' : 'none'
    }} data-id="element-1993" data-mp="3cz194ay7t5hw">
        {/* Logo / Brand area */}
        <div className="flex items-center h-14 flex-shrink-0 border-b border-gray-100" style={{
        paddingLeft: expanded ? 16 : 0,
        justifyContent: expanded ? 'flex-start' : 'center'
      }} data-id="element-1994" data-mp="2yxyjxpbxbf2r">
          <FileText className="flex-shrink-0" style={{
          color: primaryColor,
          width: 22,
          height: 22
        }} data-id="element-1995" data-mp="ut86achissxf" />
          <span className="ml-2.5 font-semibold text-gray-900 text-sm whitespace-nowrap overflow-hidden" style={{
          opacity: expanded ? 1 : 0,
          maxWidth: expanded ? 180 : 0,
          transition: 'opacity 180ms ease, max-width 220ms cubic-bezier(0.4, 0, 0.2, 1)'
        }} data-id="element-1996" data-mp="qptobga0u2hc">
            ConfigConsole
          </span>
        </div>

        {/* Navigation area */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2" data-id="element-1997" data-mp="333omll5ajzw">
          {/* Collapsed view: icon rail */}
          {!expanded && <div className="flex flex-col items-center gap-0.5 px-1" data-id="element-1998" data-mp="3uag5t0m12xft">
              {navigation.map(item => {
            if (item.href === '/roles' && currentUser.Role !== 'Super Admin') {
              return null;
            }
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
            return <button key={item.href} onClick={() => handleNavClick(item.href)} className="group flex flex-col items-center justify-center w-full rounded-md py-1.5 px-0.5 transition-colors" style={isActive ? {
              backgroundColor: 'var(--primary-50, rgba(37, 99, 235, 0.08))'
            } : undefined} title={item.name} data-id="element-1999" data-mp="2sgarkisxeyei">
                    <item.icon className="flex-shrink-0" style={{
                width: 19,
                height: 19,
                color: isActive ? primaryColor : undefined
              }} {...!isActive ? {
                className: 'flex-shrink-0 text-gray-500 group-hover:text-gray-700'
              } : {}} data-id="element-2000" data-mp="t4hdsy3ojzui" />
                    <span className="mt-0.5 leading-tight text-center truncate w-full" style={{
                fontSize: '9px',
                color: isActive ? primaryColor : '#6b7280',
                fontWeight: isActive ? 600 : 400
              }} data-id="element-2001" data-mp="j8nqcjofalxy">
                      {item.shortName || item.name}
                    </span>
                  </button>;
          })}

              {/* Super Admin icon in collapsed */}
              {currentUser.Role === 'Super Admin' && <button onClick={() => handleNavClick('/super-admin')} className="group flex flex-col items-center justify-center w-full rounded-md py-1.5 px-0.5 mt-2 transition-colors" style={location.pathname.startsWith('/super-admin') ? {
            backgroundColor: 'rgba(147, 51, 234, 0.08)'
          } : undefined} title="Super Admin" data-id="element-2002" data-mp="1o7klfnq0aa7t">
                  <Shield className="flex-shrink-0" style={{
              width: 19,
              height: 19,
              color: location.pathname.startsWith('/super-admin') ? '#7c3aed' : '#a78bfa'
            }} data-id="element-2003" data-mp="2c35i7pku4gc1" />
                  <span className="mt-0.5 leading-tight text-center truncate w-full" style={{
              fontSize: '9px',
              color: location.pathname.startsWith('/super-admin') ? '#7c3aed' : '#a78bfa',
              fontWeight: location.pathname.startsWith('/super-admin') ? 600 : 400
            }} data-id="element-2004" data-mp="1l0igx97q3c">
                    Admin
                  </span>
                </button>}
            </div>}

          {/* Expanded view: full navigation with sections */}
          {expanded && <div className="px-3 space-y-0.5" data-id="element-2005" data-mp="2pjbow83qqoid">
              {navigation.map(item => {
            if (item.href === '/roles' && currentUser.Role !== 'Super Admin') {
              lastSection = item.section;
              return null;
            }
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
            let sectionLabel = null;
            if (item.section && item.section !== lastSection) {
              sectionLabel = <div key={`section-${item.section}`} className={`px-3 pb-1 ${lastSection !== undefined ? 'pt-4' : 'pt-2'}`} data-id="element-2006" data-mp="2tsuyxm9jb8d3">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-2007" data-mp="33jplysulubzl">
                        {item.section}
                      </span>
                    </div>;
            }
            lastSection = item.section;
            return <Fragment key={item.href} data-id="element-2008" data-mp="38kwu4t0ntd6s">
                    {sectionLabel}
                    <button onClick={() => handleNavClick(item.href)} className={`group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? 'text-gray-900' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} style={isActive ? {
                backgroundColor: 'var(--primary-lighter, rgba(37, 99, 235, 0.08))'
              } : undefined} data-id="element-2009" data-mp="3fnla23ekciys">
                      <item.icon className={`mr-3 h-[18px] w-[18px] flex-shrink-0 ${!isActive ? 'text-gray-400 group-hover:text-gray-500' : ''}`} style={isActive ? {
                  color: primaryColor
                } : undefined} data-id="element-2010" data-mp="h4wi1v1jvf1p" />
                      <span className="truncate" data-id="element-2011" data-mp="v9mytmnyswdi">{item.name}</span>
                    </button>
                  </Fragment>;
          })}

              {/* Super Admin in expanded */}
              {currentUser.Role === 'Super Admin' && <button onClick={() => handleNavClick('/super-admin')} className={`group flex items-center w-full px-3 py-2 text-sm font-medium rounded-md transition-colors mt-4 whitespace-nowrap ${location.pathname.startsWith('/super-admin') ? 'bg-purple-50 text-purple-900' : 'text-purple-600 hover:bg-purple-50 hover:text-purple-900'}`} data-id="element-2012" data-mp="1izr2tv52ey51">
                  <Shield className="mr-3 h-[18px] w-[18px] flex-shrink-0" data-id="element-2013" data-mp="3upjts8r7dp3q" />
                  <span className="truncate" data-id="element-2014" data-mp="3ungxj6zgizqk">Super Admin</span>
                </button>}
            </div>}
        </nav>

        {/* User section at bottom */}
        <div className="border-t border-gray-100 flex-shrink-0" data-id="element-2015" data-mp="1hghck58dbk5q">
          {/* Collapsed: just avatar */}
          {!expanded && <div className="flex flex-col items-center py-3 gap-2" data-id="element-2016" data-mp="18h2qdhz8hvr1">
              <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0" style={{
            backgroundColor: primaryColor
          }} data-id="element-2017" data-mp="18ix8ypn19bry">
                {currentUser.User_Name?.charAt(0) || '?'}
              </div>
              <button onClick={() => setShowSignOutConfirm(true)} className="text-gray-400 hover:text-gray-600 transition-colors" title="Sign out" data-id="element-2018" data-mp="1r7u4sxy2wcca">
                <LogOut className="h-4 w-4" data-id="element-2019" data-mp="frvoqa4hgq1k" />
              </button>
            </div>}

          {/* Expanded: full user info */}
          {expanded && <div className="p-3" data-id="element-2020" data-mp="kp5cllc84tna">
              <div className="flex items-center gap-3 px-3 py-2" data-id="element-2021" data-mp="d7h6c643lm7p">
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0" style={{
              backgroundColor: primaryColor
            }} data-id="element-2022" data-mp="d9tpgmlle2mc">
                  {currentUser.User_Name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0" data-id="element-2023" data-mp="3pqfettkvvgz5">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2024" data-mp="3pn1a4eoqyvoj">
                    {currentUser.User_Name}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-2025" data-mp="3aupdsew7fay">
                    {currentUser.Role}
                  </p>
                </div>
                <button onClick={() => setShowSignOutConfirm(true)} className="text-gray-400 hover:text-gray-600 flex-shrink-0 transition-colors" title="Sign out" data-id="element-2026" data-mp="3ume19bmniowx">
                  <LogOut className="h-4 w-4" data-id="element-2027" data-mp="15x6mwffvth2c" />
                </button>
              </div>
            </div>}
        </div>
      </div>

      <ConfirmModal isOpen={showSignOutConfirm} title="Sign Out" message="Sign out of ConfigConsole?" confirmLabel="Sign Out" variant="danger" onCancel={() => setShowSignOutConfirm(false)} onConfirm={() => {
      setShowSignOutConfirm(false);
      logout();
    }} data-id="element-2028" data-mp="1atx1eyc1g4ln" />
    </>;
}